import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import FormattedNumberInput, { formatCurrency } from '../FormattedNumberInput';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRegisterTopEscapeClose } from '../../hooks/useEscapeCloseStack';
import {
  computeRentRegisterGrandTotal,
  computeRentRegisterRowTotal,
  normalizeRentRegister,
} from '../../utils/rentRegister';

export default function RentRegisterModal({ property, onClose, onSave, readOnly = false }) {
  const { t } = useLanguage();
  const unitCount = parseInt(String(property?.numberOfUnits), 10) || 0;
  const [rows, setRows] = useState(() =>
    normalizeRentRegister(property?.rentRegister, property?.numberOfUnits),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setRows(normalizeRentRegister(property?.rentRegister, property?.numberOfUnits));
  }, [property?.id, property?.numberOfUnits, property?.rentRegister]);

  const grandTotal = useMemo(() => computeRentRegisterGrandTotal(rows), [rows]);

  useRegisterTopEscapeClose(onClose);

  const updateRow = (unitNumber, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.unitNumber === unitNumber ? { ...row, [field]: value } : row)),
    );
  };

  const handleSave = async () => {
    if (readOnly || !onSave) return;
    if (unitCount <= 0) {
      setError(t('rentRegister.noUnitsError'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(rows);
      onClose();
    } catch (e) {
      setError(e?.message || t('rentRegister.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('rentRegister.title')}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {readOnly ? t('rentRegister.subtitleReadOnly') : t('rentRegister.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700"
            aria-label={t('cancel')}
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4">
          {unitCount <= 0 ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              {t('rentRegister.noUnitsHint')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px] text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 text-left">
                    <th className="py-3 pr-3 font-medium text-gray-700 w-24">{t('rentRegister.unit')}</th>
                    <th className="py-3 pr-3 font-medium text-gray-700 w-36">{t('rentRegister.rentPrice')}</th>
                    <th className="py-3 pr-3 font-medium text-gray-700 min-w-[180px]">{t('rentRegister.inclusions')}</th>
                    <th className="py-3 pr-3 font-medium text-gray-700 w-40">{t('rentRegister.ancillaryRevenue')}</th>
                    <th className="py-3 pr-3 font-medium text-gray-700 min-w-[180px]">{t('rentRegister.ancillaryDetails')}</th>
                    <th className="py-3 font-medium text-gray-700 w-32 text-right">{t('rentRegister.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const rowTotal = computeRentRegisterRowTotal(row);
                    return (
                      <tr key={row.unitNumber} className="border-b border-gray-100 align-top">
                        <td className="py-3 pr-3 font-medium text-gray-800">
                          {t('rentRegister.unitNumber').replace('{n}', String(row.unitNumber))}
                        </td>
                        <td className="py-3 pr-3">
                          <FormattedNumberInput
                            value={row.rentPrice || ''}
                            onChange={(val) => updateRow(row.unitNumber, 'rentPrice', val)}
                            className={`w-full border rounded p-2 ${readOnly ? 'bg-gray-100' : ''}`}
                            placeholder="0"
                            type="currency"
                            disabled={readOnly}
                            readOnly={readOnly}
                          />
                        </td>
                        <td className="py-3 pr-3">
                          <input
                            type="text"
                            value={row.inclusions || ''}
                            onChange={(e) => updateRow(row.unitNumber, 'inclusions', e.target.value)}
                            className={`w-full border rounded p-2 ${readOnly ? 'bg-gray-100' : ''}`}
                            placeholder={t('rentRegister.inclusionsPlaceholder')}
                            disabled={readOnly}
                            readOnly={readOnly}
                          />
                        </td>
                        <td className="py-3 pr-3">
                          <FormattedNumberInput
                            value={row.ancillaryRevenue || ''}
                            onChange={(val) => updateRow(row.unitNumber, 'ancillaryRevenue', val)}
                            className={`w-full border rounded p-2 ${readOnly ? 'bg-gray-100' : ''}`}
                            placeholder="0"
                            type="currency"
                            disabled={readOnly}
                            readOnly={readOnly}
                          />
                        </td>
                        <td className="py-3 pr-3">
                          <input
                            type="text"
                            value={row.ancillaryDetails || ''}
                            onChange={(e) => updateRow(row.unitNumber, 'ancillaryDetails', e.target.value)}
                            className={`w-full border rounded p-2 ${readOnly ? 'bg-gray-100' : ''}`}
                            placeholder={t('rentRegister.ancillaryDetailsPlaceholder')}
                            disabled={readOnly}
                            readOnly={readOnly}
                          />
                        </td>
                        <td className="py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                          {formatCurrency(rowTotal) || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5} className="pt-4 pr-3 text-right font-medium text-gray-700">
                      {t('rentRegister.grandTotal')}
                    </td>
                    <td className="pt-4 text-right font-semibold text-gray-900 whitespace-nowrap">
                      {formatCurrency(grandTotal) || '—'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {error ? (
          <p className="px-6 pb-2 text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-900"
          >
            {readOnly ? t('close') : t('cancel')}
          </button>
          {!readOnly && onSave ? (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || unitCount <= 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? t('rentRegister.saving') : t('save')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
